import { Injectable } from '@angular/core';
import { TourReport } from '../../models/tourReport.model';
import Dexie from 'dexie';
import { AppDatabaseService } from '../indexedDb/app-database.service';
import { Observable } from 'rxjs';
import { PaginationResponse } from '../../models/pagination/pagination-response.model';
import { OfflineAuthService } from '../offlineAuth/offline-auth.service';
import { ProcessTourUsers } from '../../models/processTourUsers.model';
import { FormResponseOfflineData } from '../../models/tourReportForm/formResponseOfflineData';
import { TourAnswerValidationService } from '../tourAnswer/tour-answer-validation.service';
import { Form } from '../../models/form.model';
import { TourReportStatusService } from './tour-report-status.service';
import { TourReportListParams } from '../../models/tourReportListParams.model';

@Injectable({
  providedIn: 'root'
})
export class TourReportCrudService {
  dbSet: Dexie.Table<TourReport, number>;

  constructor(
    private readonly database: AppDatabaseService,
    private readonly userService: OfflineAuthService,
    private readonly tourStatusService: TourReportStatusService,
    private readonly tourAnswerValidation: TourAnswerValidationService,
  ) {
    this.dbSet = this.database.tourReport;
  }

  addAsync(item: TourReport): Observable<TourReport> {
    return new Observable<TourReport>((observer) => {
      this.dbSet.add(item).then((result) => {
        observer.next(item);
        observer.complete();
      }).catch((error) => {
        observer.error(error);
      });
    });
  }

  addOrEditAsync(item: TourReport): Promise<number> {
    return this.dbSet.put(item);
  }

  getAllAsync(params: TourReportListParams): Observable<PaginationResponse<TourReport>> {
    return new Observable<PaginationResponse<TourReport>>((observer) => {
      void this.runOfflineTourReportListQuery(params).then(
        (response) => {
          observer.next(response);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  private async runOfflineTourReportListQuery(
    params: TourReportListParams,
  ): Promise<PaginationResponse<TourReport>> {
    const currentUser = await this.userService.getCurrentUser();
    const currentUserId = currentUser?.id ?? '';
    const fallbackOwnerNameLower = (currentUser?.username ?? '').replace('.', ' ').trim().toLowerCase();
    const matches = this.buildOfflineListMatcher(params, currentUserId, fallbackOwnerNameLower);
    const pageIndex = params.pageIndex;
    const pageSize = params.pageSize;
    const [items, totalCount] = await Promise.all([
      this.dbSet.orderBy('id').filter(matches).offset(pageIndex * pageSize).limit(pageSize).toArray(),
      this.dbSet.filter(matches).count(),
    ]);
    return { items, totalCount };
  }

  private buildOfflineListMatcher(
    params: TourReportListParams,
    currentUserId: string,
    fallbackOwnerNameLower: string,
  ): (report: TourReport) => boolean {
    const establishmentIds = params.establishmentIds ?? [];
    const startDay = params.startDate ? this.toUtcDateKey(params.startDate) : null;
    const endDay = params.endDate ? this.toUtcDateKey(params.endDate) : null;

    return (report: TourReport) =>
      this.offlineTourReportMatchesTourStates(params, report) &&
      this.offlineTourReportMatchesProcess(params, report) &&
      this.offlineTourReportMatchesUserFilters(params, report, currentUserId, fallbackOwnerNameLower) &&
      this.offlineTourReportMatchesEstablishments(establishmentIds, report) &&
      this.matchesCreationDateRange(report.creationTime, startDay, endDay);
  }

  private offlineTourReportMatchesTourStates(params: TourReportListParams, report: TourReport): boolean {
    if (!params.tourStateIds?.length) {
      return true;
    }
    return params.tourStateIds.includes(report.currentStatusId);
  }

  private offlineTourReportMatchesProcess(params: TourReportListParams, report: TourReport): boolean {
    if (params.processId === undefined || params.processId === null) {
      return true;
    }
    return report.tourType?.processId === params.processId;
  }

  private offlineTourReportMatchesUserFilters(
    params: TourReportListParams,
    report: TourReport,
    currentUserId: string,
    fallbackOwnerNameLower: string,
  ): boolean {
    if (params.tourUserIds?.length > 0) {
      return report.tourUserId != null && params.tourUserIds.includes(report.tourUserId);
    }
    if (params.currentUserTourOwner) {
      return this.matchesCurrentUserTourOwner(report, currentUserId, fallbackOwnerNameLower);
    }
    if (params.noUserAssigned) {
      return report.tourUser == null;
    }
    if (params.userName) {
      return (
        report.tourUserId != null &&
        (report.tourUser?.name?.includes(params.userName) ?? false)
      );
    }
    return true;
  }

  private offlineTourReportMatchesEstablishments(
    establishmentIds: number[],
    report: TourReport,
  ): boolean {
    if (establishmentIds.length === 0) {
      return true;
    }
    return this.isReportInEstablishmentsIds(report, establishmentIds);
  }

  private matchesCurrentUserTourOwner(
    report: TourReport,
    currentUserId: string,
    fallbackOwnerNameLower: string,
  ): boolean {
    const tourIdentity = report.tourUser?.identityUserId;
    if (currentUserId && tourIdentity) {
      return tourIdentity.toLowerCase() === currentUserId.toLowerCase();
    }
    const tourName = report.tourUser?.name?.trim().toLowerCase() ?? '';
    return tourName === fallbackOwnerNameLower && fallbackOwnerNameLower.length > 0;
  }

  private toUtcDateKey(value: Date | string): string {
    return new Date(value).toISOString().split('T')[0];
  }

  private matchesCreationDateRange(
    creationTime: Date | string,
    startDay: string | null,
    endDay: string | null,
  ): boolean {
    if (!startDay && !endDay) {
      return true;
    }
    const day = this.toUtcDateKey(creationTime);
    return this.calendarDayWithinInclusiveRange(day, startDay, endDay);
  }

  private calendarDayWithinInclusiveRange(
    day: string,
    startDay: string | null,
    endDay: string | null,
  ): boolean {
    if (startDay && day < startDay) {
      return false;
    }
    if (endDay && day > endDay) {
      return false;
    }
    return true;
  }

  private isReportInEstablishmentsIds(report: TourReport, establishmentIds: number[]) {
    return report.establishments.some((establishment) => establishmentIds.includes(establishment.id));
  }

  getTourReportById(tourReportId: number): Promise<TourReport> {
    return this.dbSet.get(tourReportId);
  }

  updateTourReportStatus(tourReportId: number, newTourReportStatus: number) {
    return this.dbSet.get(tourReportId).then((tourReport) => {
      tourReport.currentStatusId = newTourReportStatus;
      tourReport.currentStatus = this.tourStatusService.getTourStatusText(newTourReportStatus); 
      this.dbSet.put(tourReport);
    });
  }

  delete(tourReportId: number) {
    this.dbSet.delete(tourReportId);
  }

  addTourUsers(processTourUsers: ProcessTourUsers[], id: number) {
    this.dbSet.get(id).then((tourReport) => {
      tourReport.processTourUsers = processTourUsers;
      this.dbSet.put(tourReport);
    });
  }

  combineTourReports(tourReportsToCombineIds: number[]): Promise<TourReport> {
    return this.dbSet.bulkGet(tourReportsToCombineIds).then(async (tourReports) => {
      let referenceTourReport: TourReport = tourReports[0];
      let blocksIds: number[] = referenceTourReport.blocks.map(block => block.id);
      tourReports.forEach(element => {
        if(referenceTourReport.id != element.id) {
          element.blocks.forEach(block => {
            if(!blocksIds.includes(block.id))
              referenceTourReport.blocks.push(block);
          });
        }
      });

      referenceTourReport.combinedTourReports = tourReports;
      await this.dbSet.put(referenceTourReport);

      tourReports.forEach(element => {
        if(referenceTourReport.id != element.id) {
          this.dbSet.delete(element.id);
        }
      });

      return referenceTourReport;
    });
  }

  getCombinedTourReports(): Promise<TourReport[]> {
    return this.dbSet.where('combinedTourReports').notEqual(null).toArray();
  }

  restoreCombinedTourReports(data: FormResponseOfflineData, form: Form) {
    return this.dbSet.get(data.id).then((tourReports: TourReport) => {
      if(!tourReports) return;
      tourReports.combinedTourReports?.forEach(async (element: TourReport) => {
        this.dbSet.put(element);
        let newTourReportStatusId: number = this.tourAnswerValidation.getNewTourReportStatus(data, form.formFields);
        this.updateTourReportStatus(element.id, newTourReportStatusId);
      });
    });
  }
}
